import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { BodyPortal } from '../body-portal';

const DROPDOWN_WIDTH = 184;
const DROPDOWN_GAP = 6;
const DROPDOWN_ESTIMATED_HEIGHT = 168;
const VIEWPORT_MARGIN = 8;
const DAY_MINUTES = 24 * 60;

interface TimeParts {
  hours: number;
  minutes: number;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toTimeValue(hours: number, minutes: number): string {
  return `${pad2(hours)}:${pad2(minutes)}`;
}

function parseTime(value: string | null | undefined): TimeParts | null {
  if (value == null || value === '') return null;
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  // Allow 24:00 as exclusive end-of-day bound / value
  if (hours === 24 && minutes === 0) return { hours: 24, minutes: 0 };
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return { hours, minutes };
}

function toMinutes(parts: TimeParts): number {
  return parts.hours * 60 + parts.minutes;
}

function fromMinutes(total: number): TimeParts {
  if (total === DAY_MINUTES) return { hours: 24, minutes: 0 };
  const normalized = ((total % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  return {
    hours: Math.floor(normalized / 60),
    minutes: normalized % 60,
  };
}

function snapMinute(minutes: number, step: number): number {
  const safeStep = step > 0 ? step : 1;
  const snapped = Math.round(minutes / safeStep) * safeStep;
  return ((snapped % 60) + 60) % 60;
}

function digitsOnly(value: string, maxLen: number): string {
  return value.replace(/\D/g, '').slice(0, maxLen);
}

@Component({
  selector: 'tc-time-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.css',
  host: {
    '[class.tp-host--cell]': 'cell()',
  },
})
export class TimePickerComponent implements OnInit, OnDestroy {
  private readonly elRef = inject(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly vcr = inject(ViewContainerRef);
  private readonly portal = new BodyPortal(
    this.vcr,
    this.renderer,
    this.document,
  );
  private readonly onScrollCapture = () => {
    if (this.open()) this.updatePosition();
  };
  private editingField: 'hour' | 'minute' | null = null;
  private skipNextHourCommit = false;
  private skipNextMinuteCommit = false;

  @ViewChild('dropdownTpl', { read: TemplateRef })
  private dropdownTpl!: TemplateRef<unknown>;

  /** Time value as `HH:mm` (24h). */
  value = input<string>('');
  /** Earliest selectable time (`HH:mm`). */
  min = input<string>('');
  /** Latest selectable time (`HH:mm`). */
  max = input<string>('');
  cell = input(false);
  /**
   * Hour jump for ▲/▼ on hours (`1`, `2`, …). Defaults to `1`.
   */
  hourStep = input(1);
  /**
   * Minute jump for ▲/▼ on minutes (`1`, `5`, `15`, `30`, `60`).
   * Use `60` for whole hours only (minutes stay `00`). Defaults to `15`.
   */
  minuteStep = input(15);
  ariaLabel = input<string>('Výběr času');
  valueChange = output<string>();

  readonly open = signal(false);
  readonly dropdownTop = signal<number | null>(null);
  readonly dropdownBottom = signal<number | null>(null);
  readonly dropdownLeft = signal<number | null>(null);
  private readonly _hours = signal(0);
  private readonly _minutes = signal(0);
  private readonly _hasValue = signal(false);
  readonly hourDraft = signal('00');
  readonly minuteDraft = signal('00');

  readonly resolvedHourStep = computed(() =>
    Math.max(1, Math.trunc(this.hourStep()) || 1),
  );
  readonly resolvedMinuteStep = computed(() =>
    Math.min(60, Math.max(1, Math.trunc(this.minuteStep()) || 1)),
  );
  /** Whole-hour mode when `minuteStep` is 60 (or higher). */
  readonly hourOnly = computed(() => this.resolvedMinuteStep() >= 60);

  constructor() {
    effect(() => {
      const parsed = parseTime(this.value());
      const min = parseTime(this.min());
      const max = parseTime(this.max());
      const step = this.resolvedMinuteStep();
      if (parsed) {
        untracked(() => {
          const clamped = this.clampParts(parsed, min, max, step);
          this._hours.set(clamped.hours);
          this._minutes.set(this.hourOnly() ? 0 : clamped.minutes);
          this._hasValue.set(true);
          if (!this.editingField) this.syncDrafts();
        });
      } else {
        untracked(() => this._hasValue.set(false));
      }
    });
  }

  readonly displayTime = computed(() => {
    if (!this._hasValue()) return '— nevybráno —';
    return toTimeValue(this._hours(), this.hourOnly() ? 0 : this._minutes());
  });

  readonly hourLabel = computed(() => pad2(this._hours()));
  readonly minuteLabel = computed(() =>
    pad2(this.hourOnly() ? 0 : this._minutes()),
  );

  readonly canDecreaseHour = computed(() =>
    this.canStepByMinutes(-this.resolvedHourStep() * 60),
  );
  readonly canIncreaseHour = computed(() =>
    this.canStepByMinutes(this.resolvedHourStep() * 60),
  );
  readonly canDecreaseMinute = computed(() =>
    !this.hourOnly() &&
    this.canStepByMinutes(-this.resolvedMinuteStep()),
  );
  readonly canIncreaseMinute = computed(() =>
    !this.hourOnly() &&
    this.canStepByMinutes(this.resolvedMinuteStep()),
  );

  ngOnInit(): void {
    this.document.addEventListener('scroll', this.onScrollCapture, true);
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('scroll', this.onScrollCapture, true);
    this.portal.detach();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent) {
    if (
      this.open() &&
      !this.elRef.nativeElement.contains(e.target as Node) &&
      !this.portal.contains(e.target)
    ) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.close({ restoreFocus: true });
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.open()) this.updatePosition();
  }

  toggle() {
    if (this.open()) {
      this.close();
      return;
    }
    this.openDropdown();
  }

  changeHour(delta: number) {
    if (delta < 0 && !this.canDecreaseHour()) return;
    if (delta > 0 && !this.canIncreaseHour()) return;
    this.applyMinutesDelta(delta * this.resolvedHourStep() * 60);
    this.syncDrafts();
  }

  changeMinute(delta: number) {
    if (this.hourOnly()) return;
    const step = this.resolvedMinuteStep();
    if (delta < 0 && !this.canDecreaseMinute()) return;
    if (delta > 0 && !this.canIncreaseMinute()) return;
    this.applyMinutesDelta(delta * step);
    this.syncDrafts();
  }

  onFieldFocus(field: 'hour' | 'minute', event: FocusEvent) {
    this.editingField = field;
    const input = event.target as HTMLInputElement;
    queueMicrotask(() => input.select());
  }

  onHourInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const digits = digitsOnly(input.value, 2);
    input.value = digits;
    this.hourDraft.set(digits);
    if (digits.length === 2) {
      this.applyTypedHour(Number(digits));
      if (!this.hourOnly()) this.focusMinuteInput(true);
    }
  }

  onMinuteInput(event: Event) {
    if (this.hourOnly()) return;
    const input = event.target as HTMLInputElement;
    const digits = digitsOnly(input.value, 2);
    input.value = digits;
    this.minuteDraft.set(digits);
    if (digits.length === 2) {
      this.applyTypedMinute(Number(digits));
    }
  }

  onHourKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.commitHour();
      this.changeHour(1);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.commitHour();
      this.changeHour(-1);
      return;
    }
    if (
      (event.key === ':' || event.key === 'ArrowRight') &&
      !this.hourOnly()
    ) {
      event.preventDefault();
      this.commitHour();
      this.focusMinuteInput(true);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitHour();
      this.confirm();
    }
  }

  onMinuteKeydown(event: KeyboardEvent) {
    if (this.hourOnly()) return;
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.commitMinute();
      this.changeMinute(1);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.commitMinute();
      this.changeMinute(-1);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.commitMinute();
      this.focusHourInput(true);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitMinute();
      this.confirm();
    }
  }

  commitHour() {
    if (this.skipNextHourCommit) {
      this.skipNextHourCommit = false;
      return;
    }
    const raw = this.hourDraft();
    const maxHour = parseTime(this.max())?.hours === 24 ? 24 : 23;
    const hours =
      raw === '' ? this._hours() : Math.min(maxHour, Number(raw));
    this.applyTypedHour(Number.isFinite(hours) ? hours : 0);
    this.editingField = this.editingField === 'hour' ? null : this.editingField;
  }

  commitMinute() {
    if (this.skipNextMinuteCommit) {
      this.skipNextMinuteCommit = false;
      return;
    }
    const raw = this.minuteDraft();
    const minutes = raw === '' ? this._minutes() : Math.min(59, Number(raw));
    this.applyTypedMinute(Number.isFinite(minutes) ? minutes : 0);
    this.editingField =
      this.editingField === 'minute' ? null : this.editingField;
  }

  goNow() {
    const now = new Date();
    const step = this.resolvedMinuteStep();
    const candidate: TimeParts = {
      hours: now.getHours(),
      minutes: this.hourOnly() ? 0 : snapMinute(now.getMinutes(), step),
    };
    const clamped = this.clampParts(
      candidate,
      parseTime(this.min()),
      parseTime(this.max()),
      step,
    );
    this._hours.set(clamped.hours);
    this._minutes.set(this.hourOnly() ? 0 : clamped.minutes);
    this._hasValue.set(true);
    this.syncDrafts();
  }

  confirm() {
    this.commitHour();
    if (!this.hourOnly()) this.commitMinute();
    if (!this._hasValue()) {
      this.seedDefaultTime();
    } else {
      const clamped = this.clampParts(
        {
          hours: this._hours(),
          minutes: this.hourOnly() ? 0 : this._minutes(),
        },
        parseTime(this.min()),
        parseTime(this.max()),
        this.resolvedMinuteStep(),
      );
      this._hours.set(clamped.hours);
      this._minutes.set(this.hourOnly() ? 0 : clamped.minutes);
    }
    this.syncDrafts();
    this.valueChange.emit(
      toTimeValue(this._hours(), this.hourOnly() ? 0 : this._minutes()),
    );
    this.close({ restoreFocus: true });
  }

  private openDropdown() {
    if (!this._hasValue()) {
      this.seedDefaultTime();
    } else {
      const clamped = this.clampParts(
        {
          hours: this._hours(),
          minutes: this.hourOnly() ? 0 : this._minutes(),
        },
        parseTime(this.min()),
        parseTime(this.max()),
        this.resolvedMinuteStep(),
      );
      this._hours.set(clamped.hours);
      this._minutes.set(this.hourOnly() ? 0 : clamped.minutes);
    }
    this.syncDrafts();
    this.open.set(true);
    this.portal.attach(this.dropdownTpl);
    this.updatePosition();
    queueMicrotask(() => this.focusHourInput(true));
  }

  /** Empty picker opens on a whole hour (`:00`), clamped into min/max. */
  private seedDefaultTime(): void {
    const step = this.resolvedMinuteStep();
    const min = parseTime(this.min());
    const max = parseTime(this.max());
    const hour = min?.hours ?? 0;
    const wholeHour: TimeParts = { hours: hour, minutes: 0 };
    if (
      (!min || toMinutes(wholeHour) >= toMinutes(min)) &&
      (!max || toMinutes(wholeHour) <= toMinutes(max))
    ) {
      this._hours.set(hour);
      this._minutes.set(0);
    } else {
      const clamped = this.clampParts(wholeHour, min, max, step);
      this._hours.set(clamped.hours);
      this._minutes.set(this.hourOnly() ? 0 : clamped.minutes);
    }
    this._hasValue.set(true);
  }

  private close(options?: { restoreFocus?: boolean }) {
    const active = this.document.activeElement as Node | null;
    const shouldRestoreFocus =
      options?.restoreFocus === true ||
      (!!active && this.portal.contains(active));

    this.editingField = null;
    this.open.set(false);
    this.portal.detach();

    // Confirm / Escape always return focus to the trigger.
    // Outside click keeps focus wherever the user moved it.
    if (shouldRestoreFocus) {
      queueMicrotask(() => this.focusTrigger());
    }
  }

  private focusTrigger(): void {
    const trigger = this.elRef.nativeElement.querySelector(
      '.tp-trigger, .tp-trigger-cell',
    ) as HTMLElement | null;
    trigger?.focus();
  }

  private applyTypedHour(hours: number): void {
    const step = this.resolvedMinuteStep();
    const maxHour = parseTime(this.max())?.hours === 24 ? 24 : 23;
    const h = Math.max(0, Math.min(maxHour, Math.trunc(hours)));
    const minutes =
      h === 24 || this.hourOnly() ? 0 : this._minutes();
    const clamped = this.clampParts(
      { hours: h, minutes },
      parseTime(this.min()),
      parseTime(this.max()),
      step,
    );
    this._hours.set(clamped.hours);
    this._minutes.set(this.hourOnly() ? 0 : clamped.minutes);
    this._hasValue.set(true);
    this.syncDrafts();
  }

  private applyTypedMinute(minutes: number): void {
    if (this.hourOnly()) {
      this._minutes.set(0);
      this.syncDrafts();
      return;
    }
    const step = this.resolvedMinuteStep();
    const hours = this._hours() === 24 ? 24 : this._hours();
    const m =
      hours === 24 ? 0 : Math.max(0, Math.min(59, Math.trunc(minutes)));
    const clamped = this.clampParts(
      { hours, minutes: m },
      parseTime(this.min()),
      parseTime(this.max()),
      step,
    );
    this._hours.set(clamped.hours);
    this._minutes.set(clamped.minutes);
    this._hasValue.set(true);
    this.syncDrafts();
  }

  private syncDrafts(): void {
    this.hourDraft.set(pad2(this._hours()));
    this.minuteDraft.set(pad2(this.hourOnly() ? 0 : this._minutes()));
  }

  private focusHourInput(select = false): void {
    const input = this.queryDropdownInput('.tp-digit--hour');
    if (!input) return;
    this.skipNextMinuteCommit = true;
    input.focus();
    if (select) input.select();
  }

  private focusMinuteInput(select = false): void {
    const input = this.queryDropdownInput('.tp-digit--minute');
    if (!input) return;
    this.skipNextHourCommit = true;
    input.focus();
    if (select) input.select();
  }

  private queryDropdownInput(selector: string): HTMLInputElement | null {
    return this.document.querySelector(
      `.tc-body-portal ${selector}`,
    ) as HTMLInputElement | null;
  }

  private canStepByMinutes(delta: number): boolean {
    const next = toMinutes({
      hours: this._hours(),
      minutes: this._minutes(),
    }) + delta;
    const min = parseTime(this.min());
    const max = parseTime(this.max());
    if (min && next < toMinutes(min)) return false;
    if (max && next > toMinutes(max)) return false;
    if (!min && !max) {
      return true;
    }
    return next >= 0 && next <= DAY_MINUTES;
  }

  private applyMinutesDelta(delta: number): void {
    const step = this.resolvedMinuteStep();
    const min = parseTime(this.min());
    const max = parseTime(this.max());
    const current = toMinutes({
      hours: this._hours(),
      minutes: this.hourOnly() ? 0 : this._minutes(),
    });
    const next = current + delta;

    if (!min && !max) {
      const parts = fromMinutes(((next % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES);
      this._hours.set(parts.hours);
      this._minutes.set(this.hourOnly() ? 0 : parts.minutes);
      this._hasValue.set(true);
      return;
    }

    const clamped = this.clampParts(
      next >= DAY_MINUTES && max && toMinutes(max) === DAY_MINUTES
        ? { hours: 24, minutes: 0 }
        : fromMinutes(Math.max(0, Math.min(DAY_MINUTES, next))),
      min,
      max,
      step,
    );
    this._hours.set(clamped.hours);
    this._minutes.set(this.hourOnly() ? 0 : clamped.minutes);
    this._hasValue.set(true);
  }

  private clampParts(
    parts: TimeParts,
    min: TimeParts | null,
    max: TimeParts | null,
    step: number,
  ): TimeParts {
    let total = toMinutes(parts);
    if (min) total = Math.max(total, toMinutes(min));
    if (max) total = Math.min(total, toMinutes(max));

    if (step > 1) {
      const base = min ? toMinutes(min) : 0;
      const offset = total - base;
      const snappedOffset = Math.round(offset / step) * step;
      total = base + snappedOffset;
      if (min) total = Math.max(total, toMinutes(min));
      if (max) total = Math.min(total, toMinutes(max));
    }

    if (total === DAY_MINUTES) return { hours: 24, minutes: 0 };
    return fromMinutes(total);
  }

  private updatePosition(): void {
    const trigger = this.elRef.nativeElement.querySelector(
      '.tp-trigger, .tp-trigger-cell',
    ) as HTMLElement | null;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const spaceBelow = viewportH - rect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - DROPDOWN_GAP - VIEWPORT_MARGIN;
    const openUpward =
      spaceBelow < DROPDOWN_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;

    if (openUpward) {
      this.dropdownTop.set(null);
      this.dropdownBottom.set(viewportH - rect.top + DROPDOWN_GAP);
    } else {
      this.dropdownBottom.set(null);
      this.dropdownTop.set(rect.bottom + DROPDOWN_GAP);
    }

    let left = rect.left;
    if (left + DROPDOWN_WIDTH > viewportW - VIEWPORT_MARGIN) {
      left = viewportW - DROPDOWN_WIDTH - VIEWPORT_MARGIN;
    }
    if (left < VIEWPORT_MARGIN) {
      left = VIEWPORT_MARGIN;
    }
    this.dropdownLeft.set(left);
  }
}
